import { Location, User, KeyType, KeyInventoryItem, KeyTransaction, Person, KeyAssignment, BackupData, NotificationSettings, Visitor, KeyRequest } from '../types';

// API base URL - uses relative path for Azure Static Web Apps
const API_BASE = '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Generic fetch wrapper with error handling
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const response = await fetch(url, { ...defaultOptions, ...options });

  const text = await response.text();
  
  if (!response.ok && response.status !== 404) {
    // Try to parse as JSON first to get the actual error message
    let errorMessage = `HTTP ${response.status}`;
    if (text) {
      try {
        const errorJson = JSON.parse(text);
        if (errorJson.error) {
          errorMessage = errorJson.error;
        } else if (errorJson.message) {
          errorMessage = errorJson.message;
        } else {
          errorMessage = `HTTP ${response.status}: ${JSON.stringify(errorJson)}`;
        }
      } catch (e) {
        errorMessage = `HTTP ${response.status}: ${text.substring(0, 200)}`;
      }
    }
    throw new Error(errorMessage);
  }

  if (!text) {
    throw new Error('Empty response from server');
  }
  
  let json: ApiResponse<T>;
  try {
    json = JSON.parse(text);
  } catch (e) {
    throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
  }

  if (!json.success) {
    throw new Error(json.error || 'API request failed');
  }

  return json.data as T;
}

// ============ Locations API ============
export const locationsApi = {
  getAll: () => apiFetch<Location[]>('/locations'),

  getById: (id: string) => apiFetch<Location>(`/locations/${id}`),

  create: (location: Location) =>
    apiFetch<Location>('/locations', {
      method: 'POST',
      body: JSON.stringify(location),
    }),

  update: (location: Location) =>
    apiFetch<Location>(`/locations/${location.id}`, {
      method: 'PUT',
      body: JSON.stringify(location),
    }),

  delete: (id: string) =>
    apiFetch<null>(`/locations/${id}`, {
      method: 'DELETE',
    }),
};

// ============ Users API ============
export const usersApi = {
  getAll: () => apiFetch<User[]>('/users'),

  getById: (id: string) => apiFetch<User>(`/users/${id}`),

  getByEmail: (email: string) =>
    apiFetch<User>(`/users/email/${encodeURIComponent(email)}`),

  create: (user: User) =>
    apiFetch<User>('/users', {
      method: 'POST',
      body: JSON.stringify(user),
    }),

  update: (user: User) =>
    apiFetch<User>(`/users/${user.id}`, {
      method: 'PUT',
      body: JSON.stringify(user),
    }),

  delete: (id: string) =>
    apiFetch<null>(`/users/${id}`, {
      method: 'DELETE',
    }),
};

// ============ Key Types API ============
export const keyTypesApi = {
  getAll: () => apiFetch<KeyType[]>('/keytypes'),

  getById: (id: string) => apiFetch<KeyType>(`/keytypes/${id}`),

  create: (keyType: KeyType) =>
    apiFetch<KeyType>('/keytypes', {
      method: 'POST',
      body: JSON.stringify(keyType),
    }),

  update: (keyType: KeyType) =>
    apiFetch<KeyType>(`/keytypes/${keyType.id}`, {
      method: 'PUT',
      body: JSON.stringify(keyType),
    }),

  delete: (id: string) =>
    apiFetch<null>(`/keytypes/${id}`, {
      method: 'DELETE',
    }),
};

// ============ Inventory API ============
export const inventoryApi = {
  getAll: () => apiFetch<KeyInventoryItem[]>('/inventory'),

  getById: (id: string) => apiFetch<KeyInventoryItem>(`/inventory/${id}`),

  getByLocation: (locationId: string) =>
    apiFetch<KeyInventoryItem[]>(`/inventory/location/${locationId}`),

  create: (item: KeyInventoryItem) =>
    apiFetch<KeyInventoryItem>('/inventory', {
      method: 'POST',
      body: JSON.stringify(item),
    }),

  update: (item: KeyInventoryItem) =>
    apiFetch<KeyInventoryItem>(`/inventory/${item.id}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    }),

  delete: (id: string) =>
    apiFetch<null>(`/inventory/${id}`, {
      method: 'DELETE',
    }),
};

// ============ Transactions API ============
export const transactionsApi = {
  getAll: () => apiFetch<KeyTransaction[]>('/transactions'),

  getById: (id: string) => apiFetch<KeyTransaction>(`/transactions/${id}`),

  getRecent: (limit: number = 10) =>
    apiFetch<KeyTransaction[]>(`/transactions/recent?limit=${limit}`),

  getByInventoryItem: (inventoryItemId: string) =>
    apiFetch<KeyTransaction[]>(`/transactions/inventory/${inventoryItemId}`),

  create: (transaction: KeyTransaction) =>
    apiFetch<KeyTransaction>('/transactions', {
      method: 'POST',
      body: JSON.stringify(transaction),
    }),

  delete: (id: string) =>
    apiFetch<null>(`/transactions/${id}`, {
      method: 'DELETE',
    }),
};

// ============ Azure AD User Search Result ============
export interface AzureAdUserSearchResult {
  id: string;
  displayName: string;
  email: string;
  phone?: string;
  mobilePhone?: string;
  businessPhone?: string;
  department?: string;
  officeLocation?: string;
  jobTitle?: string;
  photoUrl?: string; // Base64 data URL of profile photo
}

// ============ People API ============
export const peopleApi = {
  getAll: () => apiFetch<Person[]>('/people'),

  getById: (id: string) => apiFetch<Person>(`/people/${id}`),

  getByLocation: (locationId: string) =>
    apiFetch<Person[]>(`/people/location/${locationId}`),

  create: (person: Person) =>
    apiFetch<Person>('/people', {
      method: 'POST',
      body: JSON.stringify(person),
    }),

  update: (person: Person) =>
    apiFetch<Person>(`/people/${person.id}`, {
      method: 'PUT',
      body: JSON.stringify(person),
    }),

  delete: (id: string) =>
    apiFetch<null>(`/people/${id}`, {
      method: 'DELETE',
    }),

  previewSyncFromAzureAD: (accessToken: string) =>
    apiFetch<{ preview: Array<{ azureAdId: string; name: string; email: string; phone?: string; department?: string; action: 'create' | 'update'; existingName?: string; existingEmail?: string }>; stats: { total: number; toCreate: number; toUpdate: number } }>('/people/sync/azure-ad/preview', {
      method: 'POST',
      body: JSON.stringify({ accessToken }),
    }),

  syncFromAzureAD: (accessToken: string) =>
    apiFetch<{ message: string; totalUsers: number; created: number; updated: number; skipped: number; errors: number }>('/people/sync/azure-ad', {
      method: 'POST',
      body: JSON.stringify({ accessToken }),
    }),

  search: (query: string) =>
    apiFetch<Array<{
      id: string;
      name: string;
      email?: string;
      phone?: string;
      department?: string;
      source: 'azuread' | 'cosmosdb';
      azureAdId?: string;
    }>>(`/people-search?q=${encodeURIComponent(query)}`),

  searchAzureAd: (query: string) =>
    apiFetch<AzureAdUserSearchResult[]>('/azure-ad/users/search', {
      method: 'POST',
      body: JSON.stringify({ query }),
    }),

  getAzureAdConfigStatus: () =>
    apiFetch<{
      AAD_CLIENT_ID: string;
      AAD_CLIENT_SECRET: string;
      AAD_TENANT_ID: string;
      allConfigured: boolean;
    }>('/azure-ad/config-status'),
};

// ============ Key Assignments API ============
export const assignmentsApi = {
  getAll: () => apiFetch<KeyAssignment[]>('/assignments'),

  getById: (id: string) => apiFetch<KeyAssignment>(`/assignments/${id}`),

  getByPerson: (personId: string) =>
    apiFetch<KeyAssignment[]>(`/assignments/person/${personId}`),

  getByInventory: (inventoryItemId: string) =>
    apiFetch<KeyAssignment[]>(`/assignments/inventory/${inventoryItemId}`),

  create: (assignment: KeyAssignment) =>
    apiFetch<KeyAssignment>('/assignments', {
      method: 'POST',
      body: JSON.stringify(assignment),
    }),

  update: (assignment: KeyAssignment) =>
    apiFetch<KeyAssignment>(`/assignments/${assignment.id}`, {
      method: 'PUT',
      body: JSON.stringify(assignment),
    }),

  returnKey: (id: string, returnedBy: string, notes?: string) =>
    apiFetch<KeyAssignment>(`/assignments/${id}/return`, {
      method: 'POST',
      body: JSON.stringify({ returnedBy, notes }),
    }),
};

// ============ Key Requests API ============
export const keyRequestsApi = {
  getAll: (status?: string) =>
    apiFetch<KeyRequest[]>(status ? `/key-requests?status=${status}` : '/key-requests'),

  getById: (id: string) => apiFetch<KeyRequest>(`/key-requests/${id}`),

  create: (request: Partial<KeyRequest>) =>
    apiFetch<KeyRequest>('/key-requests', {
      method: 'POST',
      body: JSON.stringify(request),
    }),

  approve: (id: string, reviewNotes?: string) =>
    apiFetch<KeyRequest>(`/key-requests/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify({ reviewNotes }),
    }),

  reject: (id: string, reviewNotes?: string) =>
    apiFetch<KeyRequest>(`/key-requests/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reviewNotes }),
    }),

  fulfill: (id: string, notes?: string) =>
    apiFetch<KeyRequest>(`/key-requests/${id}/fulfill`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    }),

  cancel: (id: string) =>
    apiFetch<null>(`/key-requests/${id}`, {
      method: 'DELETE',
    }),
};

// ============ Backup API ============
export const backupApi = {
  export: () => apiFetch<BackupData>('/backup/export'),

  import: (backup: BackupData) =>
    apiFetch<{ message: string; results: Record<string, { created: number; updated: number; errors: number }> }>('/backup/import', {
      method: 'POST',
      body: JSON.stringify(backup),
    }),
};

// ============ Database Status & Init API ============
export const databaseApi = {
  getStatus: () =>
    apiFetch<{
      initialized: boolean;
      counts: {
        locations: number;
        users: number;
        keyTypes: number;
        inventory: number;
        transactions: number;
      };
    }>('/status'),

  initialize: () =>
    apiFetch<{
      message: string;
      seeded: boolean;
      counts?: {
        locations: number;
        users: number;
        keyTypes: number;
      };
    }>('/init', {
      method: 'POST',
    }),
};

// ============ Notification Settings API ============
export const notificationSettingsApi = {
  get: () => apiFetch<Partial<NotificationSettings>>('/settings/notifications'),

  update: (settings: Partial<NotificationSettings>) =>
    apiFetch<Partial<NotificationSettings>>('/settings/notifications', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),

  testEmail: (email: string) =>
    apiFetch<{ message: string }>('/settings/notifications/test', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  getGraphStatus: () =>
    apiFetch<{
      AAD_TENANT_ID: string;
      AAD_CLIENT_ID: string;
      AAD_CLIENT_SECRET: string;
      allConfigured: boolean;
    }>('/settings/notifications/graph-status'),
};

// ============ Visitors API ============
export const visitorsApi = {
  getAll: () => apiFetch<Visitor[]>('/visitors'),

  getById: (id: string) => apiFetch<Visitor>(`/visitors/${id}`),

  getByLocation: (locationId: string) =>
    apiFetch<Visitor[]>(`/visitors/location/${locationId}`),

  getActive: () => apiFetch<Visitor[]>('/visitors/active'),

  create: (visitor: Visitor) =>
    apiFetch<Visitor>('/visitors', {
      method: 'POST',
      body: JSON.stringify(visitor),
    }),

  update: (visitor: Visitor) =>
    apiFetch<Visitor>(`/visitors/${visitor.id}`, {
      method: 'PUT',
      body: JSON.stringify(visitor),
    }),

  checkOut: (id: string, notes?: string) =>
    apiFetch<Visitor>(`/visitors/${id}/checkout`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),

  delete: (id: string) =>
    apiFetch<null>(`/visitors/${id}`, {
      method: 'DELETE',
    }),
};

// ============ Survey Import API ============
export interface SurveyPreviewKey {
  original: string;
  matched: string | null;
  keyTypeId: string | null;
}

export interface ExistingAssignment {
  keyTypeName: string;
  quantity: number;
}

export interface ExistingPersonInfo {
  id: string;
  name: string;
  email?: string;
  department?: string;
}

export interface SurveyPreviewRow {
  email: string;
  name?: string;
  keys: SurveyPreviewKey[];
  unmatchedKeys: string[];
  existingPerson: ExistingPersonInfo | null;
  existingAssignments: ExistingAssignment[];
  isConflict: boolean;
}

export interface SurveyPreviewResult {
  columns: {
    email: string | null;
    name: string | null;
    keys: string[];
  };
  rowCount: number;
  conflictCount: number;
  preview: SurveyPreviewRow[];
  allKeyTypes: string[];
}

export interface SurveyImportResult {
  email: string;
  name: string;
  status: 'created' | 'updated' | 'skipped' | 'error';
  personId?: string;
  azureAdMatched: boolean;
  keysAssigned: string[];
  error?: string;
}

export interface SurveyImportStats {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  azureAdMatched: number;
  totalKeysAssigned: number;
}

export type ConflictAction = 'overwrite' | 'merge' | 'skip';

export const surveyApi = {
  preview: (data: Record<string, string>[]) =>
    apiFetch<SurveyPreviewResult>('/survey/preview', {
      method: 'POST',
      body: JSON.stringify({ data }),
    }),

  import: (
    data: Record<string, string>[],
    importedBy: string,
    conflictResolutions: Record<string, ConflictAction>,
    defaultConflictAction: ConflictAction,
    locationId?: string
  ) =>
    apiFetch<{ stats: SurveyImportStats; results: SurveyImportResult[] }>('/survey/import', {
      method: 'POST',
      body: JSON.stringify({ data, importedBy, conflictResolutions, defaultConflictAction, locationId }),
    }),
};

// ============ Auth API ============
export interface AuthUser extends User {
  authProvider?: string;
  authId?: string;
  isNewUser?: boolean;
}

export interface StaticWebAppAuthInfo {
  clientPrincipal: {
    identityProvider: string;
    userId: string;
    userDetails: string;
    userRoles: string[];
  } | null;
}

export const authApi = {
  // Get Azure Static Web Apps auth info (from /.auth/me)
  getStaticWebAppAuth: async (): Promise<StaticWebAppAuthInfo> => {
    const response = await fetch('/.auth/me');
    return response.json();
  },

  // Get current user from our API (linked to auth identity)
  getCurrentUser: () => apiFetch<AuthUser>('/auth/me'),

  // Register a new user (link auth identity to app user)
  register: (data: { name?: string }) =>
    apiFetch<User>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Promote current user to admin (if first user)
  promoteToAdmin: () =>
    apiFetch<User>('/auth/promote', {
      method: 'POST',
    }),

  // Logout URL - go directly to Azure AD logout to end SSO session
  // Note: You must add the post_logout_redirect_uri to your app registration in Azure Portal
  // Go to: App Registration -> Authentication -> Add redirect URI for logout
  getLogoutUrl: () => {
    const tenantId = '33c1e122-3fec-49d1-aff8-2affbef66f4e';
    const appUrl = window.location.origin;
    // After Azure AD logout, redirect back to app (which will clear SWA session via 401)
    const postLogoutUri = encodeURIComponent(`${appUrl}/login`);
    // Go directly to Azure AD logout
    return `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutUri}`;
  },

  // Login URL - uses the built-in aad provider, configured via staticwebapp.config.json
  // to use our custom app registration (AAD_CLIENT_ID) instead of Microsoft's shared one
  getLoginUrl: (redirectUrl?: string) => {
    const base = '/.auth/login/aad';
    if (redirectUrl) {
      return `${base}?post_login_redirect_uri=${encodeURIComponent(redirectUrl)}`;
    }
    return base;
  },
};
